import { Stack, Group, Text, Badge, Card } from '@repletoryreach/sdk-ui';
import { TokenTextarea } from '@repletoryreach/sdk-ui/inputs';
import type { broadcasts, sms } from '@repletoryreach/sdk-api';
import { useAttributeSuggestions } from '../../../../lib/attributes';
import type { StepProps } from './shared';

/**
 * Step 2 for an SMS broadcast (4 October 2026): the text itself.
 *
 * There is no template and no number. The person writes the message, with the
 * same contact tokens a template's placeholders take (`{{contact.name|there}}`),
 * and the platform puts the business name in front of it and, for marketing,
 * the opt-out line at the end. Both count towards the parts, so the counter
 * and the preview show the message as it goes out, measured by the server
 * rather than guessed here: whether a character fits the GSM alphabet decides
 * how long each part may be, and that rule lives in one place.
 */
export function SmsMessageStep({
  state, set, errors, t, count, counting, preview, previewing, text
}: StepProps & {
  /** The server's measure of the text as it stands after the typing paused. */
  count: sms.SmsPreview | null;
  counting: boolean;
  preview: broadcasts.BroadcastPreview | null;
  previewing: boolean;
  /** The text the counter and the preview were asked about. */
  text: string;
}) {
  const suggestions = useAttributeSuggestions();
  const settled = text === state.smsBody.trim();
  const smsPreview = preview?.channel === 'sms' ? preview : null;

  /** The message as it goes out, with the business name and (marketing) the opt-out line. */
  const composed = (settled && smsPreview?.sms_preview) || count?.body || '';

  /*
   * The first sample contact's own copy. The server fills the tokens for each
   * sample with the code the sender uses; dropping that text into the composed
   * message gives what this person will actually receive. When the composed
   * message does not carry the text verbatim, the composed one is shown as it is.
   */
  const samples = (smsPreview?.sample ?? []).filter((s) => s.sms_text);
  const first = samples[0] ?? null;
  const personal = first?.sms_text && composed && text && composed.includes(text)
    ? composed.replace(text, first.sms_text)
    : null;

  const description = count
    ? [
      t(count.segments === 1 ? 'broadcasts.sms.counter.one' : 'broadcasts.sms.counter.other', {
        parts: count.segments, characters: count.characters
      }),
      count.encoding === 'ucs2' ? t('broadcasts.sms.counter.ucs2') : ''
    ].filter(Boolean).join(' ')
    : state.smsKind === 'marketing'
      ? t('broadcasts.sms.body.hintMarketing')
      : t('broadcasts.sms.body.hint');

  return (
    <div className="rr-bc-new__compose">
      <Stack gap="md">
        <TokenTextarea
          label={t('broadcasts.sms.body.label')}
          description={description}
          error={errors.smsBody}
          tokens={suggestions}
          fallbackHint={t('attributes.fallbackHint')}
          value={state.smsBody}
          // Read before the updater runs: React clears the event's
          // currentTarget once the handler returns.
          onChange={(e) => { const next = e.currentTarget.value; set('smsBody', next); }}
          minRows={6}
          autosize
        />
        {count && state.smsKind === 'marketing' && (
          <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.sms.body.optOutNote')}</Text>
        )}
      </Stack>

      <Stack gap={8} className="rr-bc-new__preview">
        <Group gap="xs">
          <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.form.preview')}</Text>
          {(previewing || counting || !settled) && <Badge size="xs" variant="light">{t('common.loading')}</Badge>}
        </Group>

        <div className="rr-bc-new__sms" aria-live="polite">
          {(personal ?? composed)
            ? <div className="rr-bc-new__sms-bubble">{personal ?? composed}</div>
            : <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('broadcasts.sms.preview.empty')}</Text>}
        </div>

        <Text role="caption" c="var(--rr-color-text-muted)">
          {personal && first
            ? t('broadcasts.new.message.previewFor', { who: first.name || first.msisdn })
            : t('broadcasts.new.message.previewNoContact')}
        </Text>

        {samples.length > 0 && settled && (
          <Card withBorder>
            <Stack gap={8}>
              <Text role="bodySmall" fw={600}>{t('broadcasts.sms.samples.title')}</Text>
              {samples.map((s) => (
                <Stack key={s.msisdn} gap={2} className="rr-bc-new__line">
                  <Text role="caption" c="var(--rr-color-text-muted)">{s.name || s.msisdn}</Text>
                  <Text role="bodySmall" className="rr-bc-new__sms-text">{s.sms_text}</Text>
                </Stack>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
    </div>
  );
}
